import React, { useEffect } from "react";
import {
	CCCheckbox,
	CCField,
	CCSelect,
	CCSelectOption,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { GuardedRichText } from "../common/GuardedRichText";
import { EventApproval, EventDoc } from "../../types/event";
import { asHtml, isEmptyRichText } from "../../utils/richText";
import { PermissionCardPreview } from "./PermissionCardPreview";
import "./eventManager.css";

interface EventPermissionsTabProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/**
 * Defaults for a new event's approval schedule, as v2 uses.
 *
 * v2 keeps these as the initial value of its local select state, so its form
 * SHOWS 30 / 7 / 3 — but its save payload falls back to 0 for all three unless
 * you actually touch a dropdown, so an untouched event stores zeros while the
 * screen claims otherwise. These are applied to the draft instead (see the
 * effect below), so what is displayed is what gets saved.
 */
const FIRST_REMINDER_DEFAULT = 30;
const SECOND_REMINDER_DEFAULT = 7;
const LOCK_DAYS_DEFAULT = 0;

/** 0-40 days, matching v2's reminder/lock day options. */
const DAY_OPTIONS: CCSelectOption[] = Array.from({ length: 41 }, (_, i) => ({
	value: String(i),
	label: `${i} days before`,
}));

const FLAGS: {
	label: string;
	/** The one flag that gates the others — see `permissionRequired` below. */
	gate?: true;
	/** Stays on screen while it is on, so a setting left over from before
	 *  permission was switched off can still be turned off. */
	showWhenOn?: true;
	get: (a: EventApproval) => boolean;
	set: (a: EventApproval, v: boolean) => EventApproval;
}[] = [
	{
		label: "Permission required",
		gate: true,
		get: (a) => a.Required === true,
		// Compulsory only means something with permission on, and its box goes
		// with it, so it is cleared here rather than left on with no way off.
		set: (a, v) => ({ ...a, Required: v, ...(v ? {} : { Compulsory: false }) }),
	},
	{
		label: "Attendance is compulsory",
		showWhenOn: true,
		get: (a) => a.Compulsory === true,
		set: (a, v) => ({ ...a, Compulsory: v }),
	},
	{
		label: "Medical update required",
		get: (a) => a.Checks?.Medical === true,
		set: (a, v) => ({ ...a, Checks: { ...(a.Checks ?? {}), Medical: v } }),
	},
	{
		label: "Emergency contact update required",
		get: (a) => a.Checks?.EmergencyContact === true,
		set: (a, v) => ({
			...a,
			Checks: { ...(a.Checks ?? {}), EmergencyContact: v },
		}),
	},
	{
		label: "Dietary requirements required",
		get: (a) => a.Checks?.Dietary === true,
		set: (a, v) => ({ ...a, Checks: { ...(a.Checks ?? {}), Dietary: v } }),
	},
];

/**
 * Permissions tab — pymble-swipe's PermissionTab concept: a row of approval
 * flags, then reminder/lock day pickers, then terms & conditions.
 *
 * Bound to the real nested Approval shape (`Checks{}` / `RSVP.ReminderDays[]`),
 * which is what the API returns and what v2's EventPermissions reads. Note the
 * `EventData` type in v2's useEventManager describes a flatter shape that the
 * API does not actually use.
 */
export const EventPermissionsTab: React.FC<EventPermissionsTabProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	const toast = useToast();
	const approval: EventApproval = event.Approval ?? {};
	const reminderDays = approval.RSVP?.ReminderDays ?? [];

	/**
	 * Everything on this tab but the first checkbox describes a permission — who
	 * must grant it, when they are reminded, what they agree to. With no
	 * permission being sought none of it applies, so it is hidden rather than
	 * left on screen to be filled in and never used. Same gating as v2.
	 *
	 * Hidden, not cleared: whatever the event already carries stays on the draft
	 * and round-trips through Save, so unticking and re-ticking gives back what
	 * was there rather than a blank form.
	 */
	const permissionRequired = approval.Required === true;

	/**
	 * Terms outlive `Required` when attendance is compulsory. v2 shows them for
	 * either, and a compulsory event's conditions are the one thing a parent
	 * still has to be shown.
	 */
	const showTerms = permissionRequired || approval.Compulsory === true;

	const patchApproval = (next: EventApproval) => onChange({ Approval: next });

	/**
	 * Fill in the approval schedule the first time an event is opened without
	 * one, so a new event starts at the school's usual 30 / 7 / 0 rather than
	 * "Not set".
	 *
	 * Only ever fills gaps: an explicit 0 is a real choice — "remind on the day"
	 * — and `?? `/`in` checks keep it, where a truthiness test would overwrite it
	 * with 30. Each slot is filled independently, so an event with only a first
	 * reminder keeps it and just gains a second.
	 *
	 * This marks the draft dirty, which is the honest outcome: the values are
	 * now part of the event and Save writes them. Skipped when read-only —
	 * someone who cannot save should not be silently changing a draft.
	 */
	useEffect(() => {
		if (readOnly) return;
		// No permission, no schedule: filling reminder days for fields that are
		// not on screen would mark the draft dirty for something nobody set.
		if (!permissionRequired) return;
		const rsvp = approval.RSVP ?? {};
		const days = rsvp.ReminderDays ?? [];
		const first = typeof days[0] === "number" ? days[0] : FIRST_REMINDER_DEFAULT;
		const second = typeof days[1] === "number" ? days[1] : SECOND_REMINDER_DEFAULT;
		const lock = typeof rsvp.LockDays === "number" ? rsvp.LockDays : LOCK_DAYS_DEFAULT;

		const alreadySet =
			first === days[0] && second === days[1] && lock === rsvp.LockDays;
		if (alreadySet) return;

		patchApproval({
			...approval,
			RSVP: { ...rsvp, ReminderDays: [first, second], LockDays: lock },
		});
		// Runs on the values it fills, and stops once they are filled.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		approval.RSVP?.ReminderDays,
		approval.RSVP?.LockDays,
		readOnly,
		permissionRequired,
	]);

	const setReminder = (index: 0 | 1, value: number) => {
		const next = [...reminderDays];
		next[index] = value;
		patchApproval({
			...approval,
			RSVP: { ...(approval.RSVP ?? {}), ReminderDays: next },
		});
	};

	return (
		<div>
			<div className="em-section-head">
				<h6 className="em-section-heading">Permissions</h6>
				<PermissionCardPreview event={event} />
			</div>
			<div className="em-grid em-mb-lg">
				{FLAGS.filter(
					(flag) =>
						flag.gate ||
						permissionRequired ||
						(flag.showWhenOn && flag.get(approval)),
				).map((flag) => (
					<div className="em-col-md-3" key={flag.label}>
						<CCCheckbox
							id={`em-flag-${flag.label}`}
							label={flag.label}
							checked={flag.get(approval)}
							disabled={readOnly}
							onChange={(e) =>
								patchApproval(flag.set(approval, e.target.checked))
							}
						/>
					</div>
				))}
			</div>

			{permissionRequired && (
				<>
					<h6 className="em-section-heading">Reminders</h6>
					<div className="em-grid em-mb-lg">
						<div className="em-col-md-4">
							<CCSelect
								id="em-first-reminder"
								label="First reminder"
								options={DAY_OPTIONS}
								placeholder="Not set"
								hint="Days before the event"
								fullWidth
								value={reminderDays[0] ?? ""}
								disabled={readOnly}
								onChange={(e) => setReminder(0, Number(e.target.value))}
							/>
						</div>

						<div className="em-col-md-4">
							<CCSelect
								id="em-second-reminder"
								label="Second reminder"
								options={DAY_OPTIONS}
								placeholder="Not set"
								hint="Days before the event"
								fullWidth
								value={reminderDays[1] ?? ""}
								disabled={readOnly}
								onChange={(e) => setReminder(1, Number(e.target.value))}
							/>
						</div>

						<div className="em-col-md-4">
							<CCSelect
								id="em-lock-days"
								label="Permissions lock"
								options={DAY_OPTIONS}
								placeholder="Not set"
								hint="Parents can no longer change their response"
								fullWidth
								value={approval.RSVP?.LockDays ?? ""}
								disabled={readOnly}
								onChange={(e) =>
									patchApproval({
										...approval,
										RSVP: {
											...(approval.RSVP ?? {}),
											LockDays: Number(e.target.value),
										},
									})
								}
							/>
						</div>
					</div>

					<h6 className="em-section-heading">External forms</h6>
					<div className="em-grid em-mb-lg">
						<div className="em-col-md-6">
							<CCField
								label="External permission form URL"
								type="url"
								fullWidth
								value={approval.ExternalPermissionForm ?? ""}
								disabled={readOnly}
								onChange={(e) =>
									patchApproval({
										...approval,
										ExternalPermissionForm: e.target.value || null,
									})
								}
							/>
						</div>
						{/* An external medical form is only collected as part of a medical
						    update, which is how v2 nests it too. */}
						{approval.Checks?.Medical === true && (
							<div className="em-col-md-6">
								<CCField
									label="External medical form URL"
									type="url"
									fullWidth
									value={approval.ExternalMedicalForm ?? ""}
									disabled={readOnly}
									onChange={(e) =>
										patchApproval({
											...approval,
											ExternalMedicalForm: e.target.value || null,
										})
									}
								/>
							</div>
						)}
					</div>
				</>
			)}

			{showTerms && (
				<>
					<h6 className="em-section-heading">Terms &amp; conditions</h6>
					<label className="em-label">
						Terms and conditions parents must accept
					</label>
					{/* Same CCRichText setup as the Details tab's notes: `editable` covers
					    read-only too, so there is no dangerouslySetInnerHTML anywhere, and
					    no uploader is wired because the event document has no image store
					    — dropped images are refused and the refusal is surfaced rather
					    than silently swallowed. */}
					{/* GuardedRichText for the same reason as the Details tab's notes,
					    and it matters more here: `Approval.*` is material to a
					    sign-off, so an editor emitting a normalised copy of the stored
					    terms could cost an untouched event its approvals on the next
					    save. The guard compares against `asHtml(...)`, the value the
					    editor was actually handed, since legacy plain text is promoted
					    to paragraphs on the way in. */}
					<GuardedRichText
						className="em-rte-capped"
						value={asHtml(approval.TermsAndConditions ?? "")}
						editable={!readOnly}
						placeholder="Conditions a parent agrees to when granting permission"
						onChange={(html: string) =>
							patchApproval({
								...approval,
								TermsAndConditions: isEmptyRichText(html) ? "" : html,
							})
						}
						onImageRefused={() =>
							toast.show({
								message:
									"Images cannot be added to terms and conditions. Link to the image instead.",
								severity: "warning",
								duration: 5000,
							})
						}
					/>
				</>
			)}
		</div>
	);
};
